let isReloading = false;

/**
 * Appends the current page's encoded content id to a request URL so the backend can scope a
 * protected-page access check to the originating page. Leaves the URL untouched when no content id
 * is available or one is already present.
 *
 * @param url The request URL.
 * @param contentId The encoded content id of the current page, from the content context.
 * @returns The URL with a `contentId` query parameter when one could be added.
 */
export function appendContentId(url: string, contentId?: string): string {
	if (!contentId || /[?&]contentId=/.test(url)) {
		return url;
	}
	const separator = url.includes('?') ? '&' : '?';
	return `${url}${separator}contentId=${encodeURIComponent(contentId)}`;
}

/**
 * Reloads the current Protected Page when a backend request reports that the member can no longer be
 * served — HTTP 401 (no longer logged in) or HTTP 403 (logged in but no longer has access). The
 * reload lets Umbraco's native Public Access redirect to the configured login / no-access page.
 *
 * The server only emits these statuses when a native page is configured to land on, so any other
 * status is left for the caller to handle. A module-level guard ensures only the first of several
 * concurrent responses triggers a reload.
 *
 * @param status The HTTP status code of the backend response.
 * @returns True when a reload was triggered (or is already in progress), so callers can skip further handling.
 */
export function reloadOnAccessLost(status: number): boolean {
	if (status !== 401 && status !== 403) {
		return false;
	}
	if (isReloading) {
		return true;
	}
	isReloading = true;
	if (typeof window !== 'undefined') window.location.reload();
	return true;
}
